import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, LogOut, ArrowLeft, Trash2, Search, Mail, CircleCheck } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYER_ROWS = [
  ["company", "Company"],
  ["position", "Position"],
  ["phone", "Phone"],
  ["property_name", "Property / Restaurant"],
  ["location", "Location"],
  ["positions_required", "Positions Required"],
  ["message", "Message"],
];

const CANDIDATE_ROWS = [
  ["phone", "Phone / WhatsApp"],
  ["location", "Current Location"],
  ["nationality", "Nationality"],
  ["current_position", "Current Position"],
  ["desired_position", "Desired Position"],
  ["years_experience", "Experience"],
  ["linkedin", "LinkedIn"],
  ["message", "Message"],
];

const Admin = () => {
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [subs, setSubs] = useState([]);
  const [tab, setTab] = useState("employer");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const q = query.trim().toLowerCase();
  const filtered = subs
    .filter((s) => statusFilter === "all" || (s.status || "new") === statusFilter)
    .filter((s) =>
      q ? Object.values(s).some((v) => typeof v === "string" && v.toLowerCase().includes(q)) : true
    );
  const counts = {
    all: subs.length,
    new: subs.filter((s) => (s.status || "new") === "new").length,
    handled: subs.filter((s) => s.status === "handled").length,
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Admin | BR Hospitality";
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : false))
      .then((d) => setUser(d || false))
      .catch(() => setUser(false));
  }, []);

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/submissions?type=${t}`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSubs(data.submissions);
      } catch {
        toast.error("Could not load submissions.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (user) load(tab);
  }, [user, tab, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Login failed");
      setUser(data);
    } catch (err) {
      setError(err.message);
    }
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(false);
  };

  const toggleStatus = async (s) => {
    const next = s.status === "handled" ? "new" : "handled";
    const res = await fetch(`${API}/submissions/${s.id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ status: next }),
    });
    if (res.ok) {
      setSubs((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: next } : x)));
    } else {
      toast.error("Could not update status.");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete this submission from ${tab === "employer" ? s.name : s.full_name}?`)) return;
    const res = await fetch(`${API}/submissions/${s.id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      setSubs((prev) => prev.filter((x) => x.id !== s.id));
      toast.success("Submission deleted.");
    } else {
      toast.error("Could not delete submission.");
    }
  };

  const downloadCv = async (s) => {
    const res = await fetch(`${API}/submissions/${s.id}/cv`, { credentials: "include" });
    if (!res.ok) return toast.error("CV not available.");
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = s.cv_filename || "cv.pdf";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (user === null) {
    return <div className="min-h-screen bg-ink" data-testid="admin-loading" />;
  }

  if (user === false) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-ivory/15 p-10"
        >
          <img src="/images/br-logo-official.jpg" alt="BR Hospitality" className="w-12 h-12 object-cover mb-6" />
          <h1 className="font-serif text-3xl font-light text-ivory">Admin Inbox</h1>
          <p className="text-[10px] tracking-[0.3em] uppercase text-bronze mt-2 mb-10">BR Hospitality</p>
          <label className="block text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Email</label>
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent border-0 border-b border-ivory/20 py-3 text-sm text-ivory focus:outline-none focus:border-bronze transition-colors duration-500 mb-6"
          />
          <label className="block text-[10px] tracking-[0.25em] uppercase text-ivory/40 mb-1">Password</label>
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent border-0 border-b border-ivory/20 py-3 text-sm text-ivory focus:outline-none focus:border-bronze transition-colors duration-500"
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-xs text-red-400">{error}</p>
          )}
          <button
            data-testid="admin-login-submit"
            type="submit"
            className="mt-8 w-full text-[11px] tracking-[0.25em] uppercase px-6 py-4 bg-bronze text-ink hover:bg-ivory transition-colors duration-500"
          >
            Sign In
          </button>
          <Link
            to="/"
            data-testid="admin-back-home"
            className="mt-6 flex items-center justify-center gap-2 text-[10px] tracking-[0.25em] uppercase text-ivory/40 hover:text-bronze transition-colors duration-500"
          >
            <ArrowLeft size={12} strokeWidth={1.5} /> Back to Site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory text-ink" data-testid="admin-inbox">
      <header className="bg-ink py-5">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/br-logo-official.jpg" alt="BR Hospitality" className="w-9 h-9 object-cover" />
            <span>
              <span className="block font-serif text-lg tracking-[0.18em] text-ivory">SUBMISSIONS INBOX</span>
              <span className="block text-[8px] tracking-[0.4em] uppercase text-bronze">BR Hospitality · Private</span>
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/" data-testid="admin-inbox-back-home" className="text-[10px] tracking-[0.25em] uppercase text-ivory/50 hover:text-bronze transition-colors duration-500">
              View Site
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-ivory/50 hover:text-bronze transition-colors duration-500"
            >
              <LogOut size={13} strokeWidth={1.5} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6 border-b border-ink/10 mb-10">
          <div className="flex gap-8">
            {[
              ["employer", "Employer Enquiries"],
              ["candidate", "Candidate Profiles"],
            ].map(([key, label]) => (
              <button
                key={key}
                data-testid={`admin-tab-${key}`}
                onClick={() => setTab(key)}
                className={`pb-4 text-[11px] tracking-[0.25em] uppercase transition-colors duration-500 border-b-2 -mb-px ${
                  tab === key ? "border-bronze text-ink" : "border-transparent text-ink/40 hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative pb-3">
            <Search size={14} strokeWidth={1.5} className="absolute left-0 top-1/2 -translate-y-[60%] text-ink/30" />
            <input
              data-testid="admin-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, company, position, location…"
              className="bg-transparent border-0 border-b border-ink/20 pl-6 pr-2 py-2 text-sm w-72 max-w-full placeholder:text-ink/30 focus:outline-none focus:border-bronze transition-colors duration-500"
            />
          </div>
        </div>

        <div className="flex gap-3 mb-8" data-testid="admin-status-filters">
          {[
            ["all", "All"],
            ["new", "New"],
            ["handled", "Handled"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-filter-${key}`}
              onClick={() => setStatusFilter(key)}
              className={`text-[10px] tracking-[0.2em] uppercase px-4 py-2 border transition-all duration-500 ${
                statusFilter === key
                  ? "border-ink bg-ink text-ivory"
                  : "border-ink/20 text-ink/50 hover:border-bronze hover:text-bronze"
              }`}
            >
              {label} · {counts[key]}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-sm text-ink/40" data-testid="admin-loading-list">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-ink/40" data-testid="admin-empty-state">
            {q
              ? `No results for "${query}".`
              : `No ${tab === "employer" ? "employer enquiries" : "candidate profiles"} yet.`}
          </p>
        ) : (
          <div className="space-y-px bg-ink/10 border border-ink/10" data-testid="admin-submissions-list">
            {filtered.map((s) => (
              <article key={s.id} data-testid={`admin-submission-${s.id}`} className={`bg-ivory p-8 lg:p-10 transition-opacity duration-500 ${s.status === "handled" ? "opacity-55" : ""}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="font-serif text-2xl font-light text-ink">
                      {tab === "employer" ? s.name : s.full_name}
                    </h2>
                    <button
                      data-testid={`admin-status-toggle-${s.id}`}
                      onClick={() => toggleStatus(s)}
                      className={`inline-flex items-center gap-1.5 text-[9px] tracking-[0.2em] uppercase px-3 py-1.5 border transition-all duration-500 ${
                        s.status === "handled"
                          ? "border-bronze/50 text-bronze bg-bronze/10"
                          : "border-ink/20 text-ink/50 hover:border-bronze hover:text-bronze"
                      }`}
                    >
                      <CircleCheck size={11} strokeWidth={1.5} />
                      {s.status === "handled" ? "Handled" : "New"}
                    </button>
                  </div>
                  <span className="text-[10px] tracking-[0.2em] uppercase text-ink/40">
                    {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                  </span>
                </div>
                <dl className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                  <div>
                    <dt className="text-[9px] tracking-[0.25em] uppercase text-ink/40">Email</dt>
                    <dd className="text-sm mt-1">
                      <a href={`mailto:${s.email}`} className="text-bronze link-underline">{s.email}</a>
                    </dd>
                  </div>
                  {(tab === "employer" ? EMPLOYER_ROWS : CANDIDATE_ROWS).map(([key, label]) =>
                    s[key] ? (
                      <div key={key}>
                        <dt className="text-[9px] tracking-[0.25em] uppercase text-ink/40">{label}</dt>
                        <dd className="text-sm mt-1 text-ink/80 whitespace-pre-wrap">{s[key]}</dd>
                      </div>
                    ) : null
                  )}
                </dl>
                <a
                  data-testid={`admin-reply-${s.id}`}
                  href={`mailto:${s.email}?subject=${encodeURIComponent(
                    tab === "employer"
                      ? "Re: Your enquiry to BR Hospitality"
                      : "Re: Your profile at BR Hospitality"
                  )}`}
                  className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase px-5 py-3 bg-ink text-ivory hover:bg-bronze hover:text-ink transition-colors duration-500"
                >
                  <Mail size={13} strokeWidth={1.5} /> Reply by Email
                </a>
                {tab === "candidate" && s.cv_filename && (
                  <button
                    data-testid={`admin-cv-download-${s.id}`}
                    onClick={() => downloadCv(s)}
                    className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase px-5 py-3 border border-ink text-ink hover:bg-ink hover:text-ivory transition-all duration-500"
                  >
                    <Download size={13} strokeWidth={1.5} /> {s.cv_filename}
                  </button>
                )}
                <button
                  data-testid={`admin-delete-${s.id}`}
                  onClick={() => remove(s)}
                  className="mt-6 ml-3 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase px-5 py-3 border border-red-900/30 text-red-900/60 hover:bg-red-900 hover:text-ivory hover:border-red-900 transition-all duration-500"
                >
                  <Trash2 size={13} strokeWidth={1.5} /> Delete
                </button>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;
